import {
  fetchAllQuestions,
  fetchArticlesAndQuestionsByCategorySlug,
  fetchArticlesAndQuestionsByInternalTags,
  fetchArticlesAndQuestionsByTags,
  fetchFeaturedArticles,
  fetchFeaturedQuestions,
  fetchHelpArticleBySlug,
  fetchHelpCategories,
} from '@/services/api/helpCenter';
import { getLocale } from 'umi';
import { extractLanguageCode } from '@/utils/intl';

const initialState = {
  articleDrawer: {},
  categories: [],
  featuredArticles: [],
  featuredQuestions: [],
  allQuestions: [],
  articlesByCategory: {},
  // tag/internalTag keyed stores (key can be a single tag or a joined multi-tag key)
  articlesByTag: {},
  questionsByTag: {},
  articlesByInternalTag: {},
  questionsByInternalTag: {},
  currentArticleCategory: null,
  currentArticleSlug: null,
  currentCategorySlug: null,
  currentTagKey: null,
  currentInternalTagKey: null,
  locale: null,
};

export default {
  namespace: 'help',

  state: initialState,
  effects: {
    * fetchHelpCategories(_, { call, put, select }) {
      const locale = extractLanguageCode(getLocale());
      const response = yield call(fetchHelpCategories, { locale });
      yield put({
        type: 'saveHelpCategories',
        payload: response.data,
      });
    },
    * fetchArticleBySlug({ payload: { slug } }, { call, put, select }) {
      const locale = extractLanguageCode(getLocale());
      const response = yield call(fetchHelpArticleBySlug, { slug, locale });
      yield put({
        type: 'saveHelpArticle',
        payload: {
          slug,
          article: response.data?.[0] || {},
        },
      });
    },
    * fetchFeaturedArticles(_, { call, put, select }) {
      const locale = extractLanguageCode(getLocale());
      const response = yield call(fetchFeaturedArticles, { locale });
      yield put({
        type: 'saveFeaturedArticles',
        payload: response.data,
      });
    },
    * fetchFeaturedQuestions(_, { call, put, select }) {
      const locale = extractLanguageCode(getLocale());
      const response = yield call(fetchFeaturedQuestions, { locale });
      yield put({
        type: 'saveFeaturedQuestions',
        payload: response.data,
      });
    },
    * fetchArticlesAndQuestionsByCategorySlug({ payload: { slug } }, { call, put, select }) {
      const locale = extractLanguageCode(getLocale());
      const [articles, questions] = yield call(fetchArticlesAndQuestionsByCategorySlug, { slug, locale });
      yield put({
        type: 'saveArticlesByCategory',
        payload: {
          slug,
          articles,
          questions
        },
      });
    },
    * fetchAllQuestions(_, { call, put, select }) {
      const locale = extractLanguageCode(getLocale());
      const response = yield call(fetchAllQuestions, { locale });
      yield put({
        type: 'saveAllQuestions',
        payload: response.data,
      });
    },
    * fetchArticlesAndQuestionsByTags({ payload: { tags, key } }, { call, put }) {
      const locale = extractLanguageCode(getLocale());
      const [articles, questions] = yield call(fetchArticlesAndQuestionsByTags, { tags, locale });
      yield put({
        type: 'saveByTag',
        payload: { key, articles, questions },
      });
      yield put({
        type: 'saveCurrentTagKey',
        payload: key,
      });
    },
    * fetchArticlesAndQuestionsByInternalTags({ payload: { internalTags, key } }, { call, put }) {
      const locale = extractLanguageCode(getLocale());
      const [articles, questions] = yield call(fetchArticlesAndQuestionsByInternalTags, { internalTags, locale });
      yield put({
        type: 'saveByInternalTag',
        payload: { key, articles, questions },
      });
      yield put({
        type: 'saveCurrentInternalTagKey',
        payload: key,
      });
    },
  },
  reducers: {
    saveHelpCategories(state, action) {
      return {
        ...state,
        categories: action.payload,
      };
    },
    saveHelpArticle(state, action) {
      return {
        ...state,
        articles: {
          ...(state.articles || {}),
          [action.payload.slug]: action.payload.article,
        },
        currentArticleCategory: action.payload.article?.category || null,
        currentArticleSlug: action.payload.slug || null,
      };
    },
    toggleHelpArticleDrawer(state, action) {
      return {
        ...state,
        articleDrawer: action.payload,
      };
    },
    saveFeaturedArticles(state, action) {
      return {
        ...state,
        featuredArticles: action.payload,
      };
    },
    saveFeaturedQuestions(state, action) {
      return {
        ...state,
        featuredQuestions: action.payload,
      };
    },
    saveArticlesByCategory(state, action) {
      return {
        ...state,
        articlesByCategory: {
          ...(state.articlesByCategory || {}),
          [action.payload.slug]: action.payload.articles,
        },
        questionsByCategory: {
          ...(state.questionsByCategory || {}),
          [action.payload.slug]: action.payload.questions,
        },
      };
    },
    saveAllQuestions(state, action) {
      return {
        ...state,
        allQuestions: action.payload,
      };
    },
    saveByTag(state, action) {
      return {
        ...state,
        articlesByTag: {
          ...(state.articlesByTag || {}),
          [action.payload.key]: action.payload.articles,
        },
        questionsByTag: {
          ...(state.questionsByTag || {}),
          [action.payload.key]: action.payload.questions,
        },
      };
    },
    saveByInternalTag(state, action) {
      return {
        ...state,
        articlesByInternalTag: {
          ...(state.articlesByInternalTag || {}),
          [action.payload.key]: action.payload.articles,
        },
        questionsByInternalTag: {
          ...(state.questionsByInternalTag || {}),
          [action.payload.key]: action.payload.questions,
        },
      };
    },
    saveLocale(state, action) {
      return {
        ...state,
        locale: action.payload,
      };
    },
    saveCurrentCategory(state, action) {
      return {
        ...state,
        currentCategorySlug: action.payload,
        currentArticleCategory: null,
        currentArticleSlug: null,
      };
    },
    saveCurrentTagKey(state, action) {
      return {
        ...state,
        currentTagKey: action.payload,
        currentCategorySlug: null,
        currentArticleCategory: null,
        currentArticleSlug: null,
      };
    },
    saveCurrentInternalTagKey(state, action) {
      return {
        ...state,
        currentInternalTagKey: action.payload,
        currentCategorySlug: null,
        currentArticleCategory: null,
        currentArticleSlug: null,
      };
    },
    clearMenuSelection(state) {
      return {
        ...state,
        currentCategorySlug: null,
        currentTagKey: null,
        currentInternalTagKey: null,
        currentArticleCategory: null,
        currentArticleSlug: null,
      };
    },
  },
};
